import { useRef, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTournament, useTournamentUpdate } from "../../TournamentContext";
import { MdDragIndicator } from "react-icons/md";
import { scheduleMatchBanner } from "../../../../functions/banners";
import {
  INDEX_TO_COURT_NAME,
  SaveStatuses,
  SCORE_DELAY,
  INPUT_SAVE_ICON_DELAY,
} from "../../../../constants";
import DeleteMatch from "./DeleteMatch";
import { firestore } from "../../../../firebase/config";
import { doc, updateDoc } from "firebase/firestore";
import SaveIndicator from "../../../../components/SaveIndicator";
import { TbExternalLink } from "react-icons/tb";

const Match = ({
  matchIndex,
  matchId,
  match,
  dayId,
  gameTime,
  numberOfCourts,
  disabled,
}) => {
  const { teams, isUserOrganizer, tournamentId } = useTournament();
  const { updateMatches } = useTournamentUpdate();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: matchId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const debounceTimers = useRef({});
  const pendingUpdates = useRef({});
  const [matchSaveStatus, setMatchSaveStatus] = useState(null);

  const handleScoreChange = (team, value) => {
    if (!team || (!value && value !== 0)) {
      return;
    }

    const scoreKey = `${team}Score`;
    const matchPath = `${matchId}.${scoreKey}`;

    setMatchSaveStatus(SaveStatuses.Loading);

    if (!pendingUpdates.current[matchId]) {
      pendingUpdates.current[matchId] = {};
    }
    pendingUpdates.current[matchId][matchPath] = value;

    if (debounceTimers.current[matchId]) {
      clearTimeout(debounceTimers.current[matchId]);
    }

    updateMatches(matchId, { [scoreKey]: value });

    debounceTimers.current[matchId] = setTimeout(async () => {
      try {
        const updates = pendingUpdates.current[matchId];
        const schedulesRef = doc(firestore, "schedules", tournamentId);
        await updateDoc(schedulesRef, updates);

        setMatchSaveStatus(SaveStatuses.Success);

        delete pendingUpdates.current[matchId];
      } catch (error) {
        alert("Failed to update match scores:", error.message);
        setMatchSaveStatus(SaveStatuses.Error);
      } finally {
        setTimeout(() => {
          setMatchSaveStatus(SaveStatuses.Null);
        }, INPUT_SAVE_ICON_DELAY);
      }
    }, SCORE_DELAY);
  };

  const Input = (team) => {
    return (
      <input
        type="number"
        className={`border rounded-md w-8 mx-1 text-center transition-colors`}
        onChange={(event) =>
          handleScoreChange(team, Number(event.target.value))
        }
        defaultValue={match[`${team}Score`]}
        disabled={!isUserOrganizer || disabled}
        min={0}
      />
    );
  };

  const TeamName = (team) => {
    const teamId = match[`${team}Id`];
    return (
      <div className="text-sm font-semibold text-stone-700 truncate cursor-default">
        {teams[teamId]?.name || teamId}
      </div>
    );
  };

  if (!match || !matchId) return null;

  return (
    <div
      id={matchId}
      data-testid={`schedule-match-${matchId}`}
      ref={setNodeRef}
      style={style}
      className="flex items-center border-t border-gray-300 relative"
    >
      {isUserOrganizer && (
        <>
          <MdDragIndicator
            {...attributes}
            {...listeners}
            className="w-6 h-full py-1 text-gray-500 cursor-grab absolute"
          />
          <DeleteMatch matchId={matchId} dayId={dayId} />
        </>
      )}

      {matchId === "break" ? (
        <div
          className={`${scheduleMatchBanner(
            null
          )} w-full flex flex-col items-center`}
        >
          <div className="w-full flex justify-center items-center text-sm font-semibold text-stone-700 truncate p-1">
            Break
          </div>
          {isUserOrganizer && <DeleteMatch matchId={matchId} dayId={dayId} />}
        </div>
      ) : (
        <div
          className={`${scheduleMatchBanner(
            teams[match?.team1Id]?.groupId
          )} w-full flex flex-col items-center`}
        >
          {match?.referee && (
            <p className="text-xs text-stone-700 -mb-1">{match?.referee}</p>
          )}

          <div className="w-full flex justify-center items-center text-center p-1">
            <div className="flex w-[47%] justify-end items-center">
              {TeamName("team1")}
              {Input("team1")}
            </div>
            <div className="w-2 flex flex-col items-center justify-center">
              {numberOfCourts > 1 && (
                <div className="text-xs text-stone-600">
                  {INDEX_TO_COURT_NAME[matchIndex % numberOfCourts]}
                </div>
              )}
              <SaveIndicator saveStatus={matchSaveStatus} />
            </div>
            <div className="flex w-[47%] justify-start items-center">
              {Input("team2")}
              {TeamName("team2")}
            </div>

            <div className="absolute top-0 right-0 text-xs text-stone-600 cursor-default">
              {gameTime}
            </div>

            {match.hyperlink && (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={match.hyperlink}
              >
                <TbExternalLink className="h-3 w-3 absolute bottom-1 right-3 text-stone-600 cursor-pointer" />
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Match;
